<script setup lang="ts">
import type { GiratudoPlacarEntry } from '@/types/giratudo';
import GiratudoPagination from '@/components/apostatudo/atoms/GiratudoPagination.vue';
import GiratudoUserPosition from '@/components/apostatudo/atoms/GiratudoUserPosition.vue';
import { getAssetsUrlForCDN } from '@/utils/template';

interface Props {
  title?: string;
  data: GiratudoPlacarEntry[];
  page: number;
  totalPages?: number;
  userPosition?: GiratudoPlacarEntry | null;
  loading?: boolean;
}

const {
  title,
  data,
  page,
  totalPages = 1,
  userPosition = null,
  loading = false,
} = defineProps<Props>();

const emit = defineEmits<{
  'update:page': [page: number];
}>();

const rowBgClasses: Record<number, string> = {
  0: 'placar-row-gold',
  1: 'placar-row-silver',
  2: 'placar-row-bronze',
};

function getPosBadgeUrl(position: number): string {
  if (position <= 3)
    return getAssetsUrlForCDN(`/assets/images/giratudo/${position}.png`);

  return getAssetsUrlForCDN('/assets/images/giratudo/4.png');
}

// ── CUSTOMIZAR AQUI: larguras das colunas ──
const { isMobile } = useLayout();

const gridCols = computed(() => {
  if (isMobile.value) {
    return '48px 1fr 52px 64px 58px';
  }

  return 'minmax(40px,64px) minmax(0,1fr) minmax(40px,70px) minmax(50px,90px) minmax(45px,75px)';
});

// ── CUSTOMIZAR AQUI: paddings ──
const rowPadding = 'mx-2';
const headerPadding = 'px-2 pt-1.5 pb-1';
</script>

<template>
  <div
    class="flex flex-col max-h-full bg-bg-primary overflow-hidden border border-[#1e3a8a]/40"
    :class="[title ? 'rounded-lg' : 'rounded-b-lg']"
  >
    <!-- Title -->
    <div
      v-if="title"
      class="shrink-0 px-3 py-3 border-b border-[#06b6d4]/30"
      style="
        background: linear-gradient(180deg, #0143e3 0%, #000717 100%);
        border-radius: 10px 10px 0 0;
      "
    >
      <h3 class="text-center text-sm font-bold tracking-widest !text-[#ffffff]">
        {{ title }}
      </h3>
    </div>

    <!-- Column Headers -->
    <div
      class="shrink-0 grid items-center border-b border-[#1e3a8a]/50 text-[10px] font-semibold uppercase tracking-widest !text-[#ffffff]"
      :class="headerPadding"
      :style="{ gridTemplateColumns: gridCols }"
    >
      <span class="text-center">POS</span>
      <span class="pl-1">NOME</span>
      <span class="text-center">PTS</span>
      <span class="text-center">PRÊMIO</span>
      <span class="text-center">CRIADO</span>
    </div>

    <!-- Table Body -->
    <div
      class="flex-1 min-h-0 overflow-y-auto placar-scrollbar py-1.5 flex flex-col gap-1"
    >
      <!-- Loading -->
      <template v-if="loading">
        <div
          v-for="i in isMobile ? 3 : 10"
          :key="`loading-${i}`"
          :class="rowPadding"
          :style="{
            height: '36px',
            minHeight: '36px',
            maxHeight: '36px',
            background: 'rgba(1, 67, 227, 0.04)',
            borderRadius: '9.827px',
          }"
        />
      </template>

      <!-- Empty -->
      <template v-else-if="data.length === 0">
        <template v-if="!isMobile">
          <div
            v-for="i in 10"
            :key="`empty-${i}`"
            :class="rowPadding"
            :style="{
              height: '36px',
              minHeight: '36px',
              maxHeight: '36px',
              background: 'rgba(1, 67, 227, 0.04)',
              borderRadius: '9.827px',
            }"
          />
        </template>
      </template>

      <!-- Rows -->
      <template v-else>
        <div
          v-for="(row, idx) in data"
          :key="row.position"
          class="grid shrink-0 items-center overflow-hidden text-[11px] text-[#ffffff]/90 transition-colors duration-150"
          :class="[rowPadding, idx < 3 && page === 1 ? rowBgClasses[idx] : '']"
          :style="{
            gridTemplateColumns: gridCols,
            height: '36px',
            minHeight: '36px',
            maxHeight: '36px',
            ...(!(idx < 3 && page === 1)
              ? {
                  background: 'rgba(1, 67, 227, 0.10)',
                  borderRadius: '9.827px',
                  border: '0 solid #FFA440',
                }
              : {}),
          }"
        >
          <!-- POS -->
          <span class="flex items-center justify-center">
            <span
              v-if="row.position <= 10"
              class="inline-flex h-7 w-7 items-center justify-center bg-contain bg-center bg-no-repeat pos-badge"
              :class="[row.position <= 3 ? 'pos-badge-top' : 'pb-1']"
              :style="{
                backgroundImage: `url(${getPosBadgeUrl(row.position)})`,
              }"
            >
              {{ row.position }}
            </span>
            <span
              v-else
              class="pos-badge pos-badge-plain"
            >
              {{ row.position }}
            </span>
          </span>

          <!-- NOME -->
          <span class="truncate pl-1 font-extrabold">
            {{ row.username }}
          </span>

          <!-- PTS -->
          <span class="truncate text-center font-semibold !text-[#ffffff]">
            {{ row.ticketNumber }}
          </span>

          <!-- PREMIO -->
          <span
            class="truncate text-center"
            :class="[
              idx < 3 && page === 1
                ? 'font-bold text-[#4ade80]'
                : 'text-[#4ade80]/80',
            ]"
          >
            {{ row.prize }}
          </span>

          <!-- CRIADO -->
          <span class="truncate text-center text-[#ffffff]/60">
            {{ row.createdAt }}
          </span>
        </div>

        <!-- Filler rows (desktop only) -->
        <template v-if="!isMobile">
          <div
            v-for="i in Math.max(0, 10 - data.length)"
            :key="`filler-${i}`"
            :class="rowPadding"
            :style="{
              height: '36px',
              minHeight: '36px',
              maxHeight: '36px',
              background: 'rgba(1, 67, 227, 0.04)',
              borderRadius: '9.827px',
            }"
          />
        </template>
      </template>
    </div>

    <!-- User Position -->
    <GiratudoUserPosition
      class="shrink-0"
      :data="
        userPosition ?? {
          position: 0,
          username: '---',
          ticketNumber: '---',
          prize: '---',
          createdAt: '---',
        }
      "
      :empty="!userPosition"
      :grid-cols="gridCols"
    />

    <!-- Pagination -->
    <GiratudoPagination
      :always-show="true"
      class="shrink-0"
      :page="page"
      :total-pages="totalPages"
      @update:page="emit('update:page', $event)"
    />
  </div>
</template>

<style scoped>
.pos-badge {
  color: #f5feff;
  text-align: center;
  text-shadow: 0.295px 0.295px 0.393px #00054c;
  -webkit-text-stroke-width: 0.2px;
  -webkit-text-stroke-color: #1d23df;
  font-size: 10.81px;
  font-style: normal;
  font-weight: 800;
  line-height: 100%;
}

.pos-badge-top {
  -webkit-text-stroke-width: 0.6px;
  font-size: 13px;
  padding-top: 6px;
  box-sizing: border-box;
}

.pos-badge-plain {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(255, 255, 255, 0.05);
  font-size: 10.81px;
  -webkit-text-stroke-width: 0;
}

.placar-row-gold {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    #f00 -12.63%,
    #ff4f15 33.35%,
    rgba(255, 79, 21, 0.5) 66.54%
  );
}

.placar-row-silver {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    #1414f0 -12.63%,
    #1414f0 33.35%,
    rgba(20, 20, 240, 0.5) 66.54%
  );
}

.placar-row-bronze {
  border-radius: 10.843px;
  background: linear-gradient(
    92deg,
    rgba(114, 114, 248, 0.9) 8.22%,
    rgba(114, 114, 248, 0.9) 50.38%,
    rgba(114, 114, 248, 0.45) 84.34%
  );
}

.placar-scrollbar::-webkit-scrollbar {
  width: 4px;
}

.placar-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.placar-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.3);
  border-radius: 2px;
}

.placar-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.5);
}
</style>
